/**
 * The one time a player is ever asked to publish anything (WALL-contracts §1). It stands under the
 * epitaph on the memorial beat, where he is already being told the man is dead, and it is asked
 * once: declining is final, and the local wall keeps the name either way.
 *
 * Nothing is published without a deliberate yes, so there is no default button, no pre-selection
 * and no "do this every time". The offer simply does not appear when there is nowhere to publish to.
 *
 * PHOTOS (PHOTOS-contracts §3) - the safeguard the owner was promised: when the man has a picture of
 * his death, the offer SHOWS that picture and asks three ways - with it, his name only, or neither.
 * Only WITH THIS PHOTO ever reads the JPEG, and if it cannot be read nothing goes up: he chose the
 * photo, so the name is never sent alone behind his back.
 */

import type { MemorialEntry } from '../../../meta/types';
import type { Ctx } from '../context';
import { box, button, el, show } from '../dom';
import type { PhotoPort } from '../photoPort';
import { photoImage } from '../photoView';
import type { WallPort } from '../wallPort';

/**
 * A picture that has not arrived in this long is not coming (a local one takes milliseconds): the
 * name alone is asked instead, well inside the beat's three-second hold when it is quick to say no.
 */
export const OFFER_PHOTO_WAIT_MS = 4000;

/** What the offer should do, decided before anything is drawn. Pure. */
export type OfferState =
  /** No name to put up, or no public wall here at all: say nothing, take up no room. */
  | { kind: 'none' }
  /** There is a wall and he could reach it, but not yet: say the one thing he can do about it. */
  | { kind: 'blocked'; why: string }
  | { kind: 'offer' };

/**
 * A name can be offered only when there IS a name (practice and the Daily Drop never make one),
 * there is a wall, and this device could publish to it. Pure.
 */
export function offerState(entry: MemorialEntry | null, port: WallPort | null): OfferState {
  if (entry === null || port === null || !port.available()) return { kind: 'none' };
  const why = port.whyNotPublish();
  return why === null ? { kind: 'offer' } : { kind: 'blocked', why };
}

/**
 * The block to put under the epitaph, or null when there is nothing to ask. `onSettled` fires once
 * the player has chosen, so the beat can let CONTINUE take the focus back.
 */
export function publishOffer(
  ctx: Ctx,
  entry: MemorialEntry | null,
  onSettled?: () => void,
): { el: HTMLElement; focus: HTMLButtonElement | null } | null {
  const port = ctx.wall ?? null;
  const state = offerState(entry, port);
  if (state.kind === 'none') return null;

  const root = el('div', 'menu-grave-offer');
  const say = (text: string): void => {
    root.replaceChildren(el('div', 'menu-grave-offer-why menu-small', text));
  };
  if (state.kind === 'blocked') {
    say(state.why);
    return { el: root, focus: null };
  }
  // Both are non-null here: `offerState` returned `offer`.
  const named = entry as MemorialEntry;
  const wall = port as WallPort;

  const settle = (text: string): void => {
    say(text);
    onSettled?.();
  };
  const nameOnly = (): void => {
    say('PUTTING HIS NAME UP...');
    void (async () => {
      const failed = await wall.publish(named);
      settle(failed === null ? 'HIS NAME IS ON THE PUBLIC MEMORIAL.' : failed);
    })();
  };
  const withPhoto = (photos: PhotoPort, id: string): void => {
    say('PUTTING HIS NAME AND PHOTO UP...');
    void (async () => {
      const jpeg = await photos.jpeg(id).catch(() => null);
      if (jpeg === null) {
        settle('HIS PHOTO COULD NOT BE READ. NOTHING WAS PUT UP.');
        return;
      }
      const failed = await wall.publish(named, jpeg);
      settle(failed === null ? 'HIS NAME AND PHOTO ARE ON THE PUBLIC MEMORIAL.' : failed);
    })();
  };

  const small = (label: string, onClick: () => void): HTMLButtonElement => button(label, onClick, 'menu-btn-small');
  const ask = (text: string): HTMLElement => el('div', 'menu-grave-offer-ask menu-small', text);
  const keep = (): HTMLButtonElement => small('KEEP HIM TO MY COMPANY', () => settle('HE STAYS WITH YOUR COMPANY.'));

  const askName = (): void => {
    root.replaceChildren(
      ask('OTHER PLAYERS MAY SEE HIS NAME, IF YOU WISH IT.'),
      box('menu-grave-offer-row', small('ADD TO THE PUBLIC MEMORIAL', nameOnly), keep()),
    );
  };
  const askWithPhoto = (photos: PhotoPort, id: string, img: HTMLImageElement): void => {
    show(img, true);
    root.replaceChildren(box(
      'menu-grave-offer-photo-row',
      img,
      box(
        'menu-grave-offer-side',
        ask('OTHER PLAYERS MAY SEE HIS NAME AND THIS PHOTO, IF YOU WISH IT.'),
        small('ADD HIM WITH THIS PHOTO', () => withPhoto(photos, id)),
        small('ADD HIS NAME ONLY', nameOnly),
        keep(),
      ),
    ));
  };

  const photos = ctx.photos;
  const id = named.photo;
  if (photos === undefined || id === undefined) askName();
  else waitForPhoto(ctx, root, photos, id, askName, (img) => askWithPhoto(photos, id, img));
  // No default: no button is focused, so nothing is published by a finger still on the key.
  return { el: root, focus: null };
}

/**
 * The question is asked only once his picture is ON SCREEN - loaded, not merely found - so WITH THIS
 * PHOTO is never pressed on a picture he has not seen. Until then the offer holds no buttons. If the
 * picture is not kept, will not load, or has not come in `OFFER_PHOTO_WAIT_MS`, the name-only offer
 * is asked instead. Either way it is drawn exactly once, so no answer ever changes under the cursor.
 */
function waitForPhoto(
  ctx: Ctx,
  root: HTMLElement,
  photos: PhotoPort,
  id: string,
  askName: () => void,
  askWithPhoto: (img: HTMLImageElement) => void,
): void {
  let asked = false;
  const once = (draw: () => void): void => {
    if (asked) return;
    asked = true;
    draw();
  };
  ctx.timers.later(OFFER_PHOTO_WAIT_MS, () => once(askName));
  void photos.url(id).then(
    (url) => {
      if (url === null || asked) {
        once(askName);
        return;
      }
      const img = photoImage('menu-grave-offer-photo', url, () => once(askName), () => once(() => askWithPhoto(img)));
      // Loading inside the offer but hidden: display:none does not stop an <img> from loading.
      show(img, false);
      root.append(img);
    },
    () => once(askName),
  );
}
